import { Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { AuthSelectors } from '@/app/+state/auth';
import { User } from '@/app/shared/interfaces';
import { ApiService } from './api.service';

interface WidgetConfig {
    enabled: boolean;
    script_url?: string;
    public_key?: string;
    identity_token?: string | null;
    identity_ttl_seconds?: number;
}

interface TaiTicketsApi {
    _q?: unknown[][];
    identify(user: Record<string, unknown> | null): void;
    setContext(ctx: Record<string, unknown>): void;
    open(): void;
    close(): void;
}

// Q&C has a single tenant, so every reporter belongs to the same organization.
const ORG_ID = 'qandc';
const ORG_NAME = 'Q&C Services';
const RETRY_REFRESH_MS = 5 * 60 * 1000;

/**
 * Loads the tai-tickets "Report a problem" widget for signed-in users and keeps
 * it told who is reporting. Keys come from the backend (GET /tickets/widget-config),
 * so each environment reports into its own project. Off when not configured.
 */
@Injectable({ providedIn: 'root' })
export class TicketsWidgetService extends ApiService {
    readonly #store = inject(Store);

    /** True while the widget is configured for the signed-in user (shows the menu item). */
    readonly enabled = signal(false);

    #user: User | null = null;
    #loading: Promise<boolean> | null = null;
    #loadingFor: string | null = null;
    #token: string | null = null;
    #refreshTimer: ReturnType<typeof setTimeout> | null = null;

    protected get basePath(): string {
        return '/tickets';
    }

    constructor() {
        super();
        this.#store
            .select(AuthSelectors.selectUser)
            .pipe(takeUntilDestroyed())
            .subscribe((user) => this.#onUser(user));
    }

    /** Open the report form (e.g. from a menu item). */
    open(): void {
        this.#tt()?.open();
    }

    #onUser(user: User | null): void {
        this.#user = user;
        if (!user) {
            this.#reset(); // next sign-in fetches a token for that user
            this.#tt()?.identify(null);
            return;
        }
        void this.#ensureLoaded(user.id).then((ok) => {
            const current = this.#user;
            if (!ok || !current || current.id !== user.id) return;
            this.#identify(current);
            this.#tt()?.setContext({ app: 'qandc', host: location.host, role: current.role });
        });
    }

    #identify(user: User): void {
        this.#tt()?.identify({
            token: this.#token,
            id: user.id,
            email: user.email,
            name: [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email,
            org_id: ORG_ID,
            org_name: ORG_NAME,
        });
    }

    #reset(): void {
        this.#clearRefresh();
        this.#token = null;
        this.#loading = null;
        this.#loadingFor = null;
        this.enabled.set(false);
    }

    #fetchConfig(): Promise<WidgetConfig | undefined> {
        return firstValueFrom(this.get<WidgetConfig>('widget-config')).then((res) => res?.data);
    }

    #ensureLoaded(userId: string): Promise<boolean> {
        if (this.#loading && this.#loadingFor === userId) return this.#loading;
        this.#clearRefresh();
        this.#loadingFor = userId;
        this.#loading = this.#fetchConfig()
            .then((cfg) => {
                if (this.#loadingFor !== userId) return false; // signed out meanwhile
                if (!cfg?.enabled || !cfg.script_url || !cfg.public_key) {
                    this.enabled.set(false);
                    return false;
                }
                this.#token = cfg.identity_token ?? null;
                this.#scheduleRefresh(cfg.identity_ttl_seconds);
                this.#loadScript(cfg.script_url, cfg.public_key);
                this.enabled.set(true);
                return true;
            })
            .catch(() => {
                if (this.#loadingFor === userId) {
                    this.#loading = null; // try again on the next sign-in
                    this.#loadingFor = null;
                }
                return false;
            });
        return this.#loading;
    }

    /** Renew the identity token before it expires (it lets the widget list "My reports"). */
    #scheduleRefresh(ttlSeconds?: number, delayMs?: number): void {
        this.#clearRefresh();
        if (!this.#token || !ttlSeconds) return;
        const userId = this.#loadingFor;
        const ms = delayMs ?? Math.max(60, ttlSeconds - 15 * 60) * 1000;
        this.#refreshTimer = setTimeout(() => {
            this.#fetchConfig()
                .then((cfg) => {
                    const user = this.#user;
                    if (!user || user.id !== userId || this.#loadingFor !== userId || !cfg?.identity_token) return;
                    this.#token = cfg.identity_token;
                    this.#identify(user);
                    this.#scheduleRefresh(cfg.identity_ttl_seconds);
                })
                .catch(() => {
                    if (this.#loadingFor === userId) this.#scheduleRefresh(ttlSeconds, RETRY_REFRESH_MS);
                });
        }, ms);
    }

    #clearRefresh(): void {
        if (this.#refreshTimer) clearTimeout(this.#refreshTimer);
        this.#refreshTimer = null;
    }

    #tt(): TaiTicketsApi | undefined {
        return (window as unknown as { TaiTickets?: TaiTicketsApi }).TaiTickets;
    }

    #loadScript(src: string, key: string): void {
        if (document.querySelector('script[data-tai-tickets-loader]')) return;
        const w = window as unknown as { TaiTickets?: TaiTicketsApi };
        // Queue calls made before the script arrives; the widget replays them.
        if (!w.TaiTickets) {
            const q: unknown[][] = [];
            w.TaiTickets = {
                _q: q,
                identify: (u) => q.push(['identify', u]),
                setContext: (c) => q.push(['setContext', c]),
                open: () => q.push(['open']),
                close: () => q.push(['close']),
            };
        }
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.dataset['key'] = key;
        s.dataset['taiTicketsLoader'] = '';
        // Look and copy come from the project's settings in the tai-tickets console.
        document.head.appendChild(s);
    }
}
